import React, { useMemo, useState } from 'react';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import { makeStyles } from '@material-ui/core/styles';
import { ClickAwayListener, Tooltip } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { DocumentTemplateType } from '../../api-client/autogenerated';
import { setSelectedDivision, setSelectedSection } from '../../features/documents/actions';
import { setDocument } from '../../features/document/actions';
import { documentTypeLookup, documentTypeToUrl } from '../../scripts/utils';
import { getDocumentsAssignedToMeState, getProjectState } from '../../features/project/selectors';
import Badge from '@material-ui/core/Badge';
import { useAppUtilsContext } from '../AppUtilsContext';

interface DrawerNestedListItemProps {
  title: string;
  listIndex: number;
  onClick: (event: React.MouseEvent<HTMLDivElement, MouseEvent>, index: number) => void;
  selected: number;
  biddingPortal?: boolean;
  projectInfoType?: string;
  disabled?: boolean;
  handlePageChange?: () => void;
  handleTypeSelectBid?: (event: React.MouseEvent<HTMLDivElement, MouseEvent>, type: string) => void;
}

const useStyles = makeStyles(() => ({
  root: {
    padding: '0px 0px 10px 9px',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    '&$selected, &$selected:hover': {
      backgroundColor: '#001A4D',
    },
  },
  biddingRoot: {
    padding: '8px 0px 0px 9px',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    '&$selected, &$selected:hover': {
      backgroundColor: '#001A4D',
    },
  },
  button: {
    height: 30,
    '&:hover': {
      backgroundColor: '#001A4D',
    },
  },
  /** The one entry carrying a second line needs the room for it. */
  buttonWithNote: {
    height: 'auto',
    minHeight: 30,
  },
  /** What CENTERLINE Drive used to be called, for as long as people are still looking for it. */
  renameNote: {
    display: 'block',
    fontSize: 10,
    fontStyle: 'italic',
    lineHeight: 1.2,
    opacity: 0.7,
  },
  selected: {
    backgroundColor: '#001A4D',
    '& *': {
      fontWeight: 700,
      color: '#FFFFFF',
    },
  },
  smallBadge: {
    fontSize: '12px',
    marginTop: '10px',
    backgroundColor: '#cc0000',
    color: 'white',
  },
}));

export default function DrawerNestedListItem(props: DrawerNestedListItemProps) {
  const classes = useStyles();
  const {
    title,
    listIndex,
    onClick,
    selected,
    biddingPortal,
    projectInfoType,
    disabled,
    handlePageChange,
    handleTypeSelectBid,
  } = props;

  const history = useHistory();
  const dispatch = useDispatch();
  const currentProject = useSelector(getProjectState);
  const documentsAssignedToMeCount = useSelector(getDocumentsAssignedToMeState);

  const {
    documentTemplates: { getTemplateId },
  } = useAppUtilsContext();

  const [tooltipOpen, setTooltipOpen] = useState(false);
  const itemDocType = title in documentTypeLookup ? documentTypeLookup[title] : title;

  // Decoration only -- `title` is the key every lookup here is keyed on, so it stays as it is.
  const isDrive = title === 'CENTERLINE Drive';

  const isSelected = () => {
    if (biddingPortal) {
      return projectInfoType === title;
    }
    const pathSplits = window.location.pathname.split('/');
    if (title === 'Bid Management') return pathSplits.includes('bid-mgmt');
    if (title === 'Construction Management') return pathSplits.includes('const-mgmt');
    if (title === 'Publishing Center') return pathSplits.includes('pub-center');
    if (title === 'CENTERLINE Drive') return pathSplits.includes('design');
    if (title === 'Manage Project') return pathSplits.includes('manage');
    if (title === 'My Notification Inbox') return pathSplits.includes('my-notifications');
    if (title === 'Correspondence Log') return pathSplits.includes('correspondence-log');
    if (title === 'Project Lifecycle') return pathSplits.includes('project-lifecycle');
    if (title === 'Project Closeout') return pathSplits.includes('project-closeout');
    if (title === 'Project Progress Reports')
      return pathSplits.includes('project-progress-reports');
    if (title === 'Quality Control Forms')
      return pathSplits.includes('project-quality-control-forms');
    if (title === 'A3 Summary') return pathSplits.includes('a3');
    return pathSplits.includes(documentTypeToUrl[itemDocType as DocumentTemplateType]);
  };

  const handleTooltipOpen = () => {
    setTooltipOpen(true);
    setTimeout(() => setTooltipOpen(false), 3000);
  };

  const handleTooltipClose = () => {
    setTooltipOpen(false);
  };

  const handleTypeSelect = async () => {
    if (itemDocType !== DocumentTemplateType.Submittals) {
      dispatch(setSelectedSection(null));
    }
    dispatch(setSelectedDivision(''));
    dispatch(setDocument(null));
    history.push(
      `/main/projects/${currentProject!.id}/documents/${
        documentTypeToUrl[itemDocType as DocumentTemplateType]
      }`,
    );
  };

  const badgeCount = useMemo(() => {
    const docKey = title in documentTypeLookup ? documentTypeLookup[title] : title;
    const templateId = getTemplateId(docKey);
    if (!templateId) return null;
    const match = documentsAssignedToMeCount.find((d) => d.document_template_id === templateId);
    return match ? match.count : null;
  }, [documentsAssignedToMeCount, title]);

  return (
    <ClickAwayListener onClickAway={handleTooltipClose}>
      <ListItem
        button
        selected={isSelected()}
        onClick={(event) => {
          if (!disabled) {
            onClick(event, listIndex);
            if (handlePageChange !== undefined) handlePageChange();
            else if (handleTypeSelectBid) handleTypeSelectBid(event, title);
            else handleTypeSelect();
          } else {
            handleTooltipOpen();
          }
        }}
        classes={{
          root: biddingPortal ? classes.biddingRoot : classes.root,
          button: isDrive ? `${classes.button} ${classes.buttonWithNote}` : classes.button,
          selected: classes.selected,
        }}
      >
        <Tooltip
          open={tooltipOpen}
          onClose={handleTooltipClose}
          arrow
          placement="left"
          title={disabled ? 'Select a project first.' : ''}
          disableHoverListener
          disableFocusListener
          disableTouchListener
        >
          <ListItemText
            secondary={
              isDrive ? (
                <>
                  {title}*<span className={classes.renameNote}>*formerly File Manager</span>
                </>
              ) : title === 'Testing' ? (
                'Testing Reports'
              ) : (
                title
              )
            }
            style={{ flexGrow: 0 }}
          />
        </Tooltip>
        {badgeCount && (
          <Badge
            badgeContent={badgeCount}
            classes={{ badge: classes.smallBadge }}
            style={{ paddingLeft: '5px' }}
          >
            <span style={{ width: 20, height: 20, display: 'inline-block' }} />
          </Badge>
        )}
      </ListItem>
    </ClickAwayListener>
  );
}
